import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import "../../../i18/config";
import { decideApproval } from "../../../api";
import { useRedux } from "../../../hooks";
import { getEventUserBadges } from "../../../redux/Event/Badge/actions";
import { Approval } from "../types";
import { formatWorkflowDate } from "../format";
import { ApprovalStatusBadge } from "./ApprovalStatusBadge";
import { ApprovalPostMedia } from "./ApprovalPostMedia";
import PreviewItem from "../../Content/PeerActivity/PreviewItem";

interface Props {
    approval: Approval | null;
    onClose: () => void;
    onDecided: (approval: Approval) => void;
    userLabel: (uuid?: string | null) => string;
}

const toLocalInput = (iso?: string | null) => {
    if (!iso) {
        return '';
    }
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * One approval: what is asked (the posts as they are now, or the workflow's
 * content), who decided what, and — for an assignee — approve / reject with a
 * comment and the editable fields.
 */
const ApprovalModal = ({ approval, onClose, onDecided, userLabel }: Props) => {
    const { t } = useTranslation(['workflow', 'peerActivityStatus']);
    const [comment, setComment] = useState('');
    const [edits, setEdits] = useState<Record<string, any>>({});
    const [busy, setBusy] = useState(false);
    const { dispatch } = useRedux();

    useEffect(() => {
        setComment('');
        setEdits({});
    }, [approval?.uuid]);

    if (!approval) {
        return null;
    }

    const canDecide = !!approval.actions?.canDecide;
    const editable = canDecide ? approval.editableFields : [];
    const firstPost = approval.posts?.[0];
    // What the approver edits: the post's current text, or the workflow payload.
    const current = (field: string) => {
        if (field in edits) {
            return edits[field];
        }
        if (firstPost && (field === 'text' || field === 'title' || field === 'activeFrom')) {
            return (firstPost as any)[field] ?? '';
        }

        return approval.payload?.[field] ?? '';
    };

    // What will go out, drawn as the post editor's preview; the approver's edits apply to
    // every post of the approval, so they show here as they are typed.
    const previews = (approval.posts || []).filter((post) => post.preview).map((post) => ({
        ...post.preview!,
        enabled: true,
        ...('title' in edits ? { title: edits.title } : {}),
        ...('text' in edits ? { text: edits.text } : {}),
    }));

    const decide = (decision: 'approve' | 'reject') => {
        if (decision === 'reject' && approval.requireRejectComment && !comment.trim()) {
            toast.error(t('workflow:rejectNeedsComment'));

            return;
        }
        setBusy(true);
        const payload: Record<string, any> = {};
        Object.entries(edits).forEach(([key, value]) => {
            payload[key] = key === 'activeFrom' && value ? new Date(value).toISOString() : value;
        });
        decideApproval(approval.uuid, { decision, comment: comment.trim() || undefined, payload: Object.keys(payload).length ? payload : undefined })
            .then((r: any) => {
                toast.success(decision === 'approve' ? t('workflow:approvedToast') : t('workflow:rejectedToast'));
                // The menu badge counts what still waits for this user; it is loaded once
                // with the layout, so it would keep the old number until a reload.
                dispatch(getEventUserBadges());
                onDecided(r.item);
            })
            .catch((e: any) => toast.error(e?.message || t('workflow:actionFailed')))
            .finally(() => setBusy(false));
    };

    const field = (name: string) => {
        if (name === 'activeFrom') {
            return <input type="datetime-local" className="form-control" value={edits.activeFrom ?? toLocalInput(current('activeFrom'))} onChange={(e) => setEdits({ ...edits, activeFrom: e.target.value })} />;
        }
        if (name === 'title') {
            return <input className="form-control" value={current('title') || ''} onChange={(e) => setEdits({ ...edits, title: e.target.value })} />;
        }

        return <textarea className="form-control" rows={6} value={current(name) || ''} onChange={(e) => setEdits({ ...edits, [name]: e.target.value })} />;
    };

    return (
        <Modal show onHide={onClose} size={previews.length > 0 ? 'xl' : 'lg'}>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    {approval.title} <ApprovalStatusBadge status={approval.status} />
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row g-4">
                    <div className={previews.length > 0 ? 'col-lg-7' : 'col-12'}>
                        <div className="small text-muted mb-3">
                            {t('workflow:requestedBy')}: {userLabel(approval.requestedBy)} · {formatWorkflowDate(approval.createdAt)}
                            {approval.timeoutAt && approval.status === 'pending' && <> · {t('workflow:decideBefore')} {formatWorkflowDate(approval.timeoutAt)}</>}
                            {approval.policy === 'all' && <> · {t('workflow:everyoneMustApprove')}</>}
                            {approval.payload?.scenario?.name && <> · {t('workflow:scenario')}: {approval.payload.scenario.name}</>}
                        </div>

                        {(approval.posts || []).map((post) => (
                            <div key={post.uuid} className="border rounded p-2 mb-2">
                                <div className="d-flex justify-content-between small mb-1">
                                    <span className="fw-semibold"><i className="ri-broadcast-line me-1" />{post.channel.name}</span>
                                    <span className="text-muted">{t('peerActivityStatus:' + post.status, { defaultValue: post.status })} · {formatWorkflowDate(post.activeFrom)}</span>
                                </div>
                                {!editable.includes('text') && <div style={{ whiteSpace: 'pre-wrap' }}>{post.title ? <strong>{post.title}<br /></strong> : null}{post.text}</div>}
                                <ApprovalPostMedia media={post.media} />
                            </div>
                        ))}

                        {!firstPost && approval.payload?.content && !editable.includes('content') &&
                            <div className="border rounded p-2 mb-2" style={{ whiteSpace: 'pre-wrap' }}>{approval.payload.content}</div>
                        }

                        {editable.map((name) => (
                            <div key={name} className="mb-3">
                                <label className="form-label">{t('workflow:editable_' + name, { defaultValue: name })}</label>
                                {field(name)}
                            </div>
                        ))}

                        {approval.decisions.length > 0 &&
                            <div className="mb-3">
                                <div className="small fw-semibold mb-1">{t('workflow:decisions')}</div>
                                {approval.decisions.map((d, i) => (
                                    <div key={i} className="small">
                                        <i className={d.decision === 'approve' ? 'ri-checkbox-circle-line text-success' : 'ri-close-circle-line text-danger'} />{' '}
                                        {userLabel(d.user)} · {formatWorkflowDate(d.at)}{d.comment ? ': ' + d.comment : ''}
                                    </div>
                                ))}
                            </div>
                        }

                        {canDecide &&
                            <div>
                                <label className="form-label">{t('workflow:comment')}{approval.requireRejectComment ? ' (' + t('workflow:requiredToReject') + ')' : ''}</label>
                                <textarea className="form-control" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
                            </div>
                        }
                    </div>
                    {previews.length > 0 &&
                        <div className="col-lg-5">
                            <div className="rounded overflow-hidden h-100">
                                <PreviewItem items={previews} isShowPreviewMobile={false} />
                            </div>
                        </div>
                    }
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={onClose}>{t('workflow:close')}</button>
                {canDecide && <>
                    <button type="button" className="btn btn-soft-danger" disabled={busy} onClick={() => decide('reject')}><i className="ri-close-line me-1" />{t('workflow:reject')}</button>
                    <button type="button" className="btn btn-success" disabled={busy} onClick={() => decide('approve')}><i className="ri-check-line me-1" />{t('workflow:approve')}</button>
                </>}
            </Modal.Footer>
        </Modal>
    );
};

export default ApprovalModal;
